import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

// import WhatshotIcon from "@material-ui/icons/Whatshot";
// import StarIcon from "@material-ui/icons/Star";
// import FiberNewIcon from "@material-ui/icons/FiberNew";

import {
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Button,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  ListItemSecondaryAction,
} from "@material-ui/core";

var formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "GBP",

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

// Separates upsell priority config from sort logic.
// Add new menu cases here; each returns an ordered array of names to pin to the top.
const getUpsellConfig = (menuName) => {
  switch (menuName) {
    case "Garlic Dough Strips":
      return [{ name: "Add Garlic Oil", label: "popular" }];

    case "Marinara":
      return [
        { name: "Add Nduja", label: "popular" },
        { name: "Add Chilli Flakes", label: "hot" },
      ];

    case "Garlic bread":
      return [
        { name: "Add Extra Cheese", label: "popular" },
        { name: "Add Nduja", label: "hot" },
      ];

    case "Margherita":
      return [];

    case "Veggie":
      return [
        { name: "Add Chilli Oil", label: "hot" },
        { name: "Add Nudja", label: "new" },
      ];

    case "Pepperoni":
      return [{ name: "Add Chilli Honey", label: "popular" }];

    case "Ham and Mushroom":
      return [{ name: "Add Chilli Oil", label: "hot" }];

    case "Hot n' Spicy":
      return [{ name: "Add Chilli Flakes", label: "hot" }];

    case "The Sizzler":
      return [
        { name: "Add Chilli Honey", label: "popular" },
        { name: "Add Nduja", label: "new" },
      ];

    case "The Meaty One":
      return [{ name: "Add Chilli Honey", label: "popular" }];

    default:
      return [];
  }
};

const frontOfHouseModificationUpsellSorter = (menuName, modifications) => {
  const upsellConfig = getUpsellConfig(menuName);

  // Build a map of name -> config for O(1) lookups in the sort
  const upsellMap = new Map(upsellConfig.map((u, i) => [u.name, { ...u, i }]));

  return [...modifications]
    .sort((a, b) => {
      const aConfig = upsellMap.get(a.name);
      const bConfig = upsellMap.get(b.name);

      if (aConfig && bConfig) return aConfig.i - bConfig.i;
      if (aConfig) return -1;
      if (bConfig) return 1;

      return a.price - b.price;
    })
    .map((item) => ({
      ...item,
      label: upsellMap.get(item.name)?.label ?? null,
    }));
};

const ModificationSelect = ({ modifications, pizza, onClick }) => {
  const original = pizza?.modifications ?? [];

  // This matches what the backend sorts by before using indexes
  const priceSorted = [...original].sort((a, b) => a.price - b.price);

  // This is the display order with upsells pinned to top
  const displaySorted = frontOfHouseModificationUpsellSorter(
    pizza?.name,
    original
  );

  return displaySorted.map((item, displayIndex) => {
    // Index relative to price-sorted array — matches backend expectation
    const backendIndex = priceSorted.findIndex((m) => m._id === item._id);

    return (
      <Mod
        key={item._id ?? displayIndex}
        {...item}
        modifications={modifications}
        index={backendIndex}
        onClick={() => onClick(backendIndex)}
      />
    );
  });
};

const useStyles = makeStyles((theme) => ({
  label: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    marginLeft: theme.spacing(1),
    padding: "1px 7px",
    borderRadius: 4,
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    verticalAlign: "middle",
    lineHeight: "18px",
  },
  labelIcon: {
    fontSize: "0.7rem !important",
  },
  new: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #a5d6a7",
  },
  popular: {
    backgroundColor: "#e8f4fe",
    color: "#1565c0",
    border: "1px solid #4da3fa",
  },
  hot: {
    backgroundColor: "#fce4ec",
    color: "#c62828",
    border: "1px solid #ef9a9a",
  },

  // Row styles per label
  listItemNew: {
    backgroundColor: "#f1faf1",
    borderLeft: "4px solid #a5d6a7",
    "&:hover": { backgroundColor: "#e0f5e0" },
  },
  listItemPopular: {
    backgroundColor: "#f0f8ff",
    borderLeft: "4px solid #4da3fa",
    "&:hover": { backgroundColor: "#dff0ff" },
  },
  listItemHot: {
    backgroundColor: "#fff5f7",
    borderLeft: "4px solid #ef9a9a",
    "&:hover": { backgroundColor: "#ffe8ed" },
  },
  listItemDefault: {
    borderLeft: "4px solid transparent",
  },

  upsellName: {
    fontWeight: 600,
  },
}));
const LABEL_CONFIG = {
  new: { cls: "new", rowCls: "listItemNew" },
  popular: { cls: "popular", rowCls: "listItemPopular" },
  hot: { cls: "hot", rowCls: "listItemHot" },
};

const Mod = ({ name, price, _id, modifications, onClick, index, label }) => {
  const classes = useStyles();
  const config = label ? LABEL_CONFIG[label.toLowerCase()] : null;

  return (
    <ListItem
      dense
      button
      onClick={onClick}
      className={config ? classes[config.rowCls] : classes.listItemDefault}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          checked={modifications.indexOf(index) !== -1}
          tabIndex={-1}
          disableRipple
          color="primary"
        />
      </ListItemIcon>

      <ListItemText
        primary={
          <span className={config ? classes.upsellName : undefined}>
            {name}
            {config && (
              <span className={`${classes.label} ${classes[config.cls]}`}>
                {label}
              </span>
            )}
          </span>
        }
      />

      <ListItemSecondaryAction>
        <Typography variant="body2" color="textSecondary">
          (+{formatter.format(price)})
        </Typography>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default ModificationSelect;
